import { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from 'reactstrap';
import { useOutletContext } from 'react-router-dom';
import {
  withLoading,
  useTranslator,
  InputCheckboxControlled as InputCheckbox,
  useAppContext,
} from '@front-activerse/common-components';
import Layout from '../../Layout/Layout';
import FavMealsFilters from './FavMealsFilters';
import FavMeal from './FavMeal';
import InsertToDayPlan from '../Common/InsertMealToDayPlan';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';
import TitleBar from '../../Layout/TitleBar';

const FavMealsIndexContainer = ({
  lastAvailableDay,
  favMeals,
  addMealToFavorites,
  removeMealFromFavorites,
  insertFavoriteToDayplan,
  refetch,
  currentDietId,
}) => {
  const { trackFirebaseEvent } = useOutletContext();
  const { t } = useTranslator();
  const {
    images: { favouritesIcon2, noSearchResultsIcon },
  } = useAppContext();

  const [active, setActive] = useState('all');
  const [search, setSearch] = useState('');
  const [mealId, setMealId] = useState(undefined);
  const [useSearchResult, setUseSearchResult] = useState(false);
  const [showInsertDate, setShowInsertDate] = useState(false);

  const [showOnlyCurrentDiet, setShowOnlyCurrentDiet] = useState(true);

  useEffect(() => {
    let favouritesNumber = 0;
    favMeals.forEach((meal) => {
      if (meal.name === active || active === 'all') {
        favouritesNumber += 1;
      }
    });

    trackFirebaseEvent('screen_view', {
      firebase_screen: 'Favourites',
      favourites_category: active,
      favourites_number: favouritesNumber,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event) => {
    if (event.target.name === 'showOnlyCurrentDiet') {
      setShowOnlyCurrentDiet(event.target.checked);

      trackFirebaseEvent('user_action', {
        action: 'tap-diet_phase_meals ',
        location: 'Favourites ',
      });
    } else {
      setSearch(event.target.value);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await refetch({ text: search });
    if (search) {
      setUseSearchResult(true);
    } else {
      setUseSearchResult(false);
    }

    trackFirebaseEvent('user_action', {
      action: 'search_favourites',
      location: 'Favourites ',
    });
  };

  const openInsertDate = (mealId) => {
    if (mealId) {
      setMealId(mealId);
      setShowInsertDate(true);

      trackFirebaseEvent('user_action', {
        action: 'tap-add_to_diet',
        meal_id: mealId,
      });
    }
  };

  const closeInsertDate = () => {
    setShowInsertDate(false);
  };

  const handleReset = async () => {
    setSearch('');
    await refetch({ text: '' });
    setUseSearchResult(false);
  };

  const renderFavMeals = () => {
    if (favMeals) {
      if (useSearchResult) {
        if (favMeals.length === 0) {
          return (
            <div className="text-center pt-xl">
              <img src={noSearchResultsIcon} width={64} height={64} className="mb-m" alt="" />
              <p className="text-text-variant-color mb-m">
                {t('meal.searchResults.noResultsContent')}
              </p>
              <p className="text-text-variant-color">
                {t('meal.searchResults.noResultsAdditionalContent')}
              </p>
            </div>
          );
        }
      }

      if (favMeals.length > 0) {
        const filteredFavMeals = favMeals.filter(
          (meal) =>
            ((meal.name === active || active === 'all') &&
              showOnlyCurrentDiet &&
              meal.dietId === currentDietId) ||
            (!showOnlyCurrentDiet && (meal.name === active || active === 'all'))
        );

        if (filteredFavMeals.length === 0 && showOnlyCurrentDiet) {
          return (
            <Row className="pt-5">
              <Col md={{ size: 6, offset: 3 }} className="text-center">
                <p className="text-text-variant-color mb-m">
                  {t('noMealsForSelectedDiet.content')}
                </p>
                <Button
                  color="link"
                  className="text-link-color"
                  onClick={() => setShowOnlyCurrentDiet(false)}
                >
                  {t('noMealsForSelectedDiet.button')}
                </Button>
              </Col>
            </Row>
          );
        }

        return (
          <Row className="mealplan-fav-meals">
            {filteredFavMeals.map((meal) => (
              <Col xs="6" md="3" key={meal.id}>
                <FavMeal
                  meal={meal}
                  toggleInsertDate={(mealId) => openInsertDate(mealId)}
                  removeMealFromFavorites={removeMealFromFavorites}
                  addMealToFavorites={addMealToFavorites}
                />
              </Col>
            ))}
          </Row>
        );
      }

      return (
        <Row className="align-items-center full-height3">
          <Col xs={{ size: 10, offset: 1 }} md={{ size: 6, offset: 3 }} className="text-center">
            <img src={favouritesIcon2} alt="" />
            <p className="text-text-variant-color mt-2">
              {t('mealPlan.favorites.noMeals.content')}
            </p>
          </Col>
        </Row>
      );
    }

    return '';
  };

  const filteredFavMealsForCategories = favMeals.filter(
    (meal) => (showOnlyCurrentDiet && meal.dietId === currentDietId) || !showOnlyCurrentDiet
  );

  return (
    <Layout>
      <TitleBar>{t('mealPlan.favorites.navbarTitle')}</TitleBar>
      <div className="page-template-fav-meals full-height3a">
        <Container className="py-md-4">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                {((favMeals && favMeals.length > 0) || useSearchResult) && (
                  <FavMealsFilters
                    favMeals={filteredFavMealsForCategories}
                    active={active}
                    setActive={setActive}
                    search={search}
                    handleChange={(event) => handleChange(event)}
                    handleSubmit={(event) => handleSubmit(event)}
                    handleReset={handleReset}
                    useSearchResult={useSearchResult}
                  />
                )}
                <div className="fav-result-container">
                  {favMeals && favMeals.length > 0 && (
                    <div className="grey-box mb-xl">
                      <InputCheckbox
                        label={t('mealPlan.favorites.compatibilityCheckbox.description')}
                        id="showOnlyCurrentDiet"
                        name="showOnlyCurrentDiet"
                        value={showOnlyCurrentDiet}
                        handleChange={(event) => handleChange(event)}
                        checked={showOnlyCurrentDiet}
                      />
                    </div>
                  )}

                  <h2 className="title-medium semi-bold mb-m">
                    {useSearchResult && t('meal.searchResults.title')}
                  </h2>
                  {renderFavMeals()}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <InsertToDayPlan
        lastAvailableDay={lastAvailableDay}
        showInsertDate={showInsertDate}
        mealId={mealId}
        insertFavoriteToDayplan={insertFavoriteToDayplan}
        closeInsertDate={() => closeInsertDate()}
      />
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="mealPlan.favorites.navbarTitle" />;

export default withLoading(FavMealsIndexContainer, Placeholder);
